// "Itinerary" card of a charter booking: origin + departure | flight time | destination + arrival.
import type { ReactNode } from 'react';
import { Card, Col, Descriptions, Row, Typography } from 'antd';
import dayjs from 'dayjs';

import type { CharterBookingDetail } from '../../api/types';
import { fmtFlightTime } from '../../data/duration';
import { fmtZoned } from '../../data/airportTime';

const { Text } = Typography;

// SHAR-735 — the airport as the ShareJet itinerary writes it: code, full airport
// name, city, country. The charter screens printed the code, city and country
// and left the name out, so the same flight read differently on the two screens.
function airportFull(
  code: string | null,
  name: string | null,
  city: string | null,
  country: string | null,
): string {
  return [code, name, city, country].filter(Boolean).join(' — ').replace(' — ', ' — ') || '—';
}

export function ItineraryCard({
  b,
  extra,
}: {
  b: CharterBookingDetail;
  extra: ReactNode;
}) {
  return (
    <Card
      title="Itinerary"
      extra={extra}
    >
      <Row gutter={24}>
        <Col xs={24} sm={8}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Origin airport">
              {airportFull(b.originCode, b.originName, b.originCity, b.originCountry)}
            </Descriptions.Item>
            <Descriptions.Item label="Departure date and time">
              {fmtZoned(b.departureZoned, dayjs(b.departureDateTime).format('DD/MM/YYYY HH:mm'))}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} sm={8}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Estimated flight time">
              {fmtFlightTime(b.estFlightTimeHours)}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} sm={8}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Destination airport">
              {airportFull(b.destCode, b.destName, b.destCity, b.destCountry)}
            </Descriptions.Item>
            <Descriptions.Item label="Arrival date and time">
              {b.arrivalDateTime ? fmtZoned(b.arrivalZoned, dayjs(b.arrivalDateTime).format('DD/MM/YYYY HH:mm')) : '—'}
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
      {b.itineraryEdited ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Edited on this booking. The quote still reads {b.quotedItinerary.originCode} →{' '}
          {b.quotedItinerary.destCode} ·{' '}
          {fmtZoned(b.quotedItinerary.departureZoned, dayjs(b.quotedItinerary.departureDateTime).format('DD/MM/YYYY HH:mm'))}.
        </Text>
      ) : null}
    </Card>
  );
}
